/**
 * The router — the live half of gate 9's three-way sync (manifest ↔ router ↔
 * docs/05). Routes here appear in routes.manifest.ts; CI cross-checks both
 * against the docs/05 table.
 *
 * The auth boundary: everything under the shell requires an active session;
 * pending users are routed to /pending and match nothing else (docs/01 §1).
 */

import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useRouteError } from 'react-router';

import { ErrorState, SkeletonCard } from '../components/ui/index.js';
import { DeniedScreen, LoginScreen, PendingScreen } from '../features/auth/screens.js';
import { DashboardScreen } from '../features/dashboard/DashboardScreen.js';
import type { HistoryScope } from '../features/history/api.js';
import { AppShell } from '../features/shell/AppShell.js';

import { isStaleChunkError, reloadForStaleBuild } from './staleBuild.js';
import { useMe } from './useMe.js';

// Route-level code splitting (docs/03 §10): each feature area loads as its own
// chunk. Auth, shell, and dashboard stay in the entry bundle — they are the
// first paint for every session.
const designModule = () => import('../features/design/DesignSystemScreen.js');
const propertiesList = () => import('../features/properties/PropertiesListScreen.js');
const propertiesCreate = () => import('../features/properties/PropertyCreateScreen.js');
const propertiesDetail = () => import('../features/properties/PropertyDetailScreen.js');
const propertyVendorsTab = () => import('../features/properties/PropertyVendorsTab.js');
const propertiesEdit = () => import('../features/properties/PropertyEditScreen.js');
const tenantsList = () => import('../features/tenants/TenantsListScreen.js');
const tenantsCreate = () => import('../features/tenants/TenantCreateScreen.js');
const tenantsDetail = () => import('../features/tenants/TenantDetailScreen.js');
const tenancyDetail = () => import('../features/tenants/TenancyDetailScreen.js');
const leaseForm = () => import('../features/tenants/LeaseFormScreen.js');
const vendorsList = () => import('../features/vendors/VendorsListScreen.js');
const vendorsCreate = () => import('../features/vendors/VendorCreateScreen.js');
const vendorsDetail = () => import('../features/vendors/VendorDetailScreen.js');
const agreementScreens = () => import('../features/vendors/AgreementScreens.js');
const utilityAccountScreens = () => import('../features/vendors/UtilityAccountScreens.js');
const utilityAccountEditors = () => import('../features/vendors/UtilityAccountEditors.js');
const groupsModule = () => import('../features/properties/GroupScreens.js');
const searchModule = () => import('../features/search/SearchScreen.js');
const workModule = () => import('../features/work/WorkScreen.js');
const adminHome = () => import('../features/admin/AdminHomeScreen.js');
const adminUsers = () => import('../features/admin/AdminUsersScreen.js');
const adminUserDetail = () => import('../features/admin/AdminUserDetailScreen.js');
const adminRoles = () => import('../features/admin/AdminRoleScreens.js');
const adminReference = () => import('../features/admin/AdminReferenceScreen.js');
const adminNameFix = () => import('../features/admin/AdminNameFixScreen.js');
const adminImport = () => import('../features/admin/AdminImportScreen.js');
const adminAudit = () => import('../features/admin/AdminAuditScreen.js');
const adminQa = () => import('../features/admin/AdminQaScreen.js');
const adminPeopleRecon = () => import('../features/admin/AdminPeopleReconciliationScreen.js');
const adminViewers = () => import('../features/admin/AdminViewerScreens.js');
const contactsModule = () => import('../features/contacts/ContactScreens.js');
const notesModule = () => import('../features/notes/NotesCard.js');
const documentsModule = () => import('../features/documents/DocumentScreens.js');
const documentsTab = () => import('../features/documents/DocumentsTab.js');
const historyModule = () => import('../features/history/HistoryTab.js');
const keyDateModule = () => import('../features/key-dates/KeyDateScreen.js');
const actionItemsTab = () => import('../features/key-dates/PropertyActionItemsTab.js');
const completedArchive = () => import('../features/key-dates/CompletedArchive.js');
const managementModule = () => import('../features/management/ManagementScreens.js');

function lazyScreen<P extends object>(
  load: () => Promise<Record<string, unknown>>,
  name: string,
): ComponentType<P> {
  return lazy(async () => {
    const mod = await load();
    // `mod` itself can be undefined: a swallowed preload error resolves rather
    // than rejects, and reading through it would throw an opaque TypeError
    // instead of something recognisable.
    const screen = (mod as Record<string, unknown> | undefined)?.[name];
    // A module that loads but has no such export means the bundle and this file
    // disagree — a half-applied deploy, or a rename that missed a call site.
    // Without this the failure surfaced as "Cannot read properties of undefined
    // (reading 'PropertyDetailScreen')", which names nothing actionable and is
    // not recognisable as a stale build. Phrased so staleBuild.ts matches it.
    if (typeof screen !== 'function') {
      throw new Error(`Failed to fetch dynamically imported module: ${name} is not in this build`);
    }
    return { default: screen as ComponentType<P> };
  }) as unknown as ComponentType<P>;
}

const DesignSystemScreen = lazyScreen(designModule, 'DesignSystemScreen');
const PropertiesListScreen = lazyScreen(propertiesList, 'PropertiesListScreen');
const PropertyCreateScreen = lazyScreen(propertiesCreate, 'PropertyCreateScreen');
const PropertyDetailScreen = lazyScreen(propertiesDetail, 'PropertyDetailScreen');
const OverviewTab = lazyScreen(propertiesDetail, 'OverviewTab');
const PropertyTenantsTab = lazyScreen(propertiesDetail, 'PropertyTenantsTab');
const PropertyVendorsTab = lazyScreen(propertyVendorsTab, 'PropertyVendorsTab');
const PropertyEditScreen = lazyScreen(propertiesEdit, 'PropertyEditScreen');
const TenantsListScreen = lazyScreen(tenantsList, 'TenantsListScreen');
const TenantCreateScreen = lazyScreen(tenantsCreate, 'TenantCreateScreen');
const TenantDetailScreen = lazyScreen(tenantsDetail, 'TenantDetailScreen');
const TenantOverviewTab = lazyScreen(tenantsDetail, 'TenantOverviewTab');
const TenantTenanciesTab = lazyScreen(tenantsDetail, 'TenantTenanciesTab');
const TenancyDetailScreen = lazyScreen(tenancyDetail, 'TenancyDetailScreen');
const TenancyOverviewTab = lazyScreen(tenancyDetail, 'TenancyOverviewTab');
const TenancyContactsTab = lazyScreen(tenancyDetail, 'TenancyContactsTab');
const LeaseFormScreen = lazyScreen(leaseForm, 'LeaseFormScreen');
const VendorsListScreen = lazyScreen(vendorsList, 'VendorsListScreen');
const VendorCreateScreen = lazyScreen(vendorsCreate, 'VendorCreateScreen');
const VendorDetailScreen = lazyScreen(vendorsDetail, 'VendorDetailScreen');
const VendorOverviewTab = lazyScreen(vendorsDetail, 'VendorOverviewTab');
const VendorAgreementsTab = lazyScreen(vendorsDetail, 'VendorAgreementsTab');
const VendorPropertiesTab = lazyScreen(vendorsDetail, 'VendorPropertiesTab');
const VendorAccountsTab = lazyScreen(vendorsDetail, 'VendorAccountsTab');
const VendorUtilityPropertiesTab = lazyScreen(vendorsDetail, 'VendorUtilityPropertiesTab');
const VendorContactsTab = lazyScreen(vendorsDetail, 'VendorContactsTab');
const UtilityAccountCreateScreen = lazyScreen(utilityAccountEditors, 'UtilityAccountCreateScreen');
const UtilityAccountEditScreen = lazyScreen(utilityAccountEditors, 'UtilityAccountEditScreen');
const AgreementCreateScreen = lazyScreen(agreementScreens, 'AgreementCreateScreen');
const AgreementDetailScreen = lazyScreen(agreementScreens, 'AgreementDetailScreen');
const AgreementEditScreen = lazyScreen(agreementScreens, 'AgreementEditScreen');
const UtilityAccountScreen = lazyScreen(utilityAccountScreens, 'UtilityAccountScreen');
const AdminHomeScreen = lazyScreen(adminHome, 'AdminHomeScreen');
const AdminUsersScreen = lazyScreen(adminUsers, 'AdminUsersScreen');
const AdminUserDetailScreen = lazyScreen(adminUserDetail, 'AdminUserDetailScreen');
const AdminRolesScreen = lazyScreen(adminRoles, 'AdminRolesScreen');
const AdminRoleDetailScreen = lazyScreen(adminRoles, 'AdminRoleDetailScreen');
const AdminReferenceScreen = lazyScreen(adminReference, 'AdminReferenceScreen');
const AdminNameFixScreen = lazyScreen(adminNameFix, 'AdminNameFixScreen');
const AdminImportScreen = lazyScreen(adminImport, 'AdminImportScreen');
const AdminAuditScreen = lazyScreen(adminAudit, 'AdminAuditScreen');
const AdminQaScreen = lazyScreen(adminQa, 'AdminQaScreen');
const AdminPeopleReconciliationScreen = lazyScreen(
  adminPeopleRecon,
  'AdminPeopleReconciliationScreen',
);
const AdminReleasesScreen = lazyScreen(adminViewers, 'AdminReleasesScreen');
const AdminRegistryScreen = lazyScreen(adminViewers, 'AdminRegistryScreen');
const ContactsListScreen = lazyScreen(contactsModule, 'ContactsListScreen');
const ContactDetailScreen = lazyScreen(contactsModule, 'ContactDetailScreen');
const EntityNotesTab = lazyScreen<{ entityType: string }>(notesModule, 'EntityNotesTab');
// One History tab for every record (docs/11 F-1). The scope is the API path
// segment, typed so a typo is a compile error rather than a tab that renders
// empty against a 404.
const RecordHistoryTab = lazyScreen<{ scope: HistoryScope }>(historyModule, 'RecordHistoryTab');
const KeyDateScreen = lazyScreen(keyDateModule, 'KeyDateScreen');
const PropertyActionItemsTab = lazyScreen(actionItemsTab, 'PropertyActionItemsTab');
const CompletedArchive = lazyScreen(completedArchive, 'CompletedArchive');
const ManagementAgreementScreen = lazyScreen(managementModule, 'ManagementAgreementScreen');
const ManagementTermsTab = lazyScreen(managementModule, 'ManagementTermsTab');
const DocumentsScreen = lazyScreen(documentsModule, 'DocumentsScreen');
const DocumentRefScreen = lazyScreen(documentsModule, 'DocumentRefScreen');
const DocumentOverviewTab = lazyScreen(documentsModule, 'DocumentOverviewTab');
const EntityDocumentsTab = lazyScreen<{ entityType: string }>(documentsTab, 'EntityDocumentsTab');
const GroupDetailScreen = lazyScreen(groupsModule, 'GroupDetailScreen');
const GroupPropertiesTab = lazyScreen(groupsModule, 'GroupPropertiesTab');
const GroupEditScreen = lazyScreen(groupsModule, 'GroupEditScreen');
const SearchScreen = lazyScreen(searchModule, 'SearchScreen');
const WorkScreen = lazyScreen(workModule, 'WorkScreen');
const WorkList = lazyScreen<{ kind: 'overdue' | 'due-soon' | 'insights' }>(workModule, 'WorkList');

/** Chunk-loading fallback matching the final layout (docs/03 §3). */
function ScreenFallback(): ReactNode {
  return (
    <div className="mx-auto max-w-3xl">
      <SkeletonCard rows={4} />
    </div>
  );
}

function suspended(node: ReactNode): ReactNode {
  return <Suspense fallback={<ScreenFallback />}>{node}</Suspense>;
}

/** Auth boundary: loading skeleton -> login -> pending -> the shell. */
function Boundary(): ReactNode {
  const { me, error } = useMe();
  if (error !== null && error !== undefined) {
    return (
      <div className="p-6">
        <ErrorState
          body="The server did not respond. Check your connection."
          onRetry={() => {
            window.location.reload();
          }}
        />
      </div>
    );
  }
  if (me === undefined) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <SkeletonCard rows={4} />
      </div>
    );
  }
  if (me === null) return <LoginScreen />;
  if (me.status === 'pending') return <PendingScreen email={me.email} />;
  return <AppShell me={me} />;
}

function DashboardRoute(): ReactNode {
  const { me } = useMe();
  return me !== null && me !== undefined ? <DashboardScreen me={me} /> : null;
}

function RouteError(): ReactNode {
  const error = useRouteError();

  // A deploy landed while this tab was open, so the screen's chunk no longer
  // exists (see app/staleBuild.ts). Reload once instead of showing the raw
  // "Failed to fetch dynamically imported module" — which told a user nothing
  // and, because Try again only re-rendered, could not be escaped.
  if (isStaleChunkError(error) && reloadForStaleBuild()) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <SkeletonCard rows={4} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <ErrorState
        title={isStaleChunkError(error) ? 'This tab is out of date' : undefined}
        body={
          isStaleChunkError(error)
            ? 'PropOS was updated while this tab was open. Reload to get the current version.'
            : error instanceof Error
              ? error.message
              : 'This screen failed to load.'
        }
        onRetry={() => {
          window.location.reload();
        }}
      />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginScreen /> },
  {
    path: '/',
    element: <Boundary />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <DashboardRoute /> },
      {
        path: 'work',
        element: suspended(<WorkScreen />),
        children: [
          { index: true, element: <Navigate to="/work/overdue" replace /> },
          { path: 'overdue', element: suspended(<WorkList kind="overdue" />) },
          { path: 'due-soon', element: suspended(<WorkList kind="due-soon" />) },
          { path: 'insights', element: suspended(<WorkList kind="insights" />) },
          // Where finished work goes (punch 2.3). A lane rather than a screen
          // of its own: it is the same module, read backwards.
          { path: 'completed', element: suspended(<CompletedArchive />) },
        ],
      },
      { path: 'properties', element: suspended(<PropertiesListScreen />) },
      { path: 'properties/new', element: suspended(<PropertyCreateScreen />) },
      {
        path: 'properties/:id',
        element: suspended(<PropertyDetailScreen />),
        children: [
          { index: true, element: suspended(<OverviewTab />) },
          { path: 'tenants', element: suspended(<PropertyTenantsTab />) },
          { path: 'vendors', element: suspended(<PropertyVendorsTab />) },
          // Everything this property governs that has a date on it (punch 2.2).
          { path: 'action-items', element: suspended(<PropertyActionItemsTab />) },
          // The old tab, kept as a REDIRECT rather than deleted: it is in
          // people's history and in links pasted into Teams, and "who works
          // here" is the same page it used to point at (docs/11 F-5).
          { path: 'agreements', element: <Navigate to="../vendors" replace /> },
          { path: 'documents', element: suspended(<EntityDocumentsTab entityType="property" />) },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="property" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="properties" />) },
        ],
      },
      { path: 'properties/:id/edit', element: suspended(<PropertyEditScreen />) },
      { path: 'tenants', element: suspended(<TenantsListScreen />) },
      { path: 'tenants/new', element: suspended(<TenantCreateScreen />) },
      {
        path: 'tenants/:id',
        element: suspended(<TenantDetailScreen />),
        children: [
          { index: true, element: suspended(<TenantOverviewTab />) },
          { path: 'tenancies', element: suspended(<TenantTenanciesTab />) },
          { path: 'documents', element: suspended(<EntityDocumentsTab entityType="tenant_org" />) },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="tenant_org" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="tenant-orgs" />) },
        ],
      },
      {
        // One tenant at one property. Matt 2026-08-25: from here you need "the
        // tenant information and lease information ... including lease, lease
        // documents, other tenant-linked documents, and tenant contacts", so it
        // gets the same tab set every other record in the app has rather than
        // being the one leaf page with nowhere to go.
        path: 'tenancies/:id',
        element: suspended(<TenancyDetailScreen />),
        children: [
          { index: true, element: suspended(<TenancyOverviewTab />) },
          { path: 'contacts', element: suspended(<TenancyContactsTab />) },
          { path: 'documents', element: suspended(<EntityDocumentsTab entityType="tenancy" />) },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="tenancy" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="tenancies" />) },
        ],
      },
      /**
       * ONE lease form, at two paths (docs/17 wave 3B). `new` also serves a
       * renewal through `?renew=<leaseId>`, which seeds the successor from the
       * incumbent — a renewal is a NEW lease on the same tenancy, not an edit
       * of the old one, so it cannot be a third route pretending otherwise.
       *
       * It replaces `/tenancies/:id/lease/abstract`: the abstract is the
       * form's second tab now (Matt, 2026-08-25: "lease abstracts are really
       * attributes to the lease, not a separate object / section").
       */
      { path: 'tenancies/:id/lease/new', element: suspended(<LeaseFormScreen />) },
      { path: 'tenancies/:id/lease/:leaseId', element: suspended(<LeaseFormScreen />) },
      { path: 'vendors', element: suspended(<VendorsListScreen />) },
      { path: 'vendors/new', element: suspended(<VendorCreateScreen />) },
      // A FULL EDITOR, not a tab (mailbox RM-012), so it is registered BEFORE
      // 'vendors/:id' and outside its children — a tab strip and a breadcrumb
      // over a long form is a different shell from a record with tabs.
      {
        path: 'vendors/:id/accounts/new',
        element: suspended(<UtilityAccountCreateScreen />),
      },
      {
        path: 'vendors/:id',
        element: suspended(<VendorDetailScreen />),
        children: [
          { index: true, element: suspended(<VendorOverviewTab />) },
          { path: 'agreements', element: suspended(<VendorAgreementsTab />) },
          { path: 'properties', element: suspended(<VendorPropertiesTab />) },
          { path: 'accounts', element: suspended(<VendorAccountsTab />) },
          { path: 'utility-properties', element: suspended(<VendorUtilityPropertiesTab />) },
          { path: 'contacts', element: suspended(<VendorContactsTab />) },
          { path: 'documents', element: suspended(<EntityDocumentsTab entityType="vendor" />) },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="vendor" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="vendors" />) },
        ],
      },
      // One key date, worked over time: the facts, its progress notes and the
      // close control (docs/11 F-3). Reached from any record's card and from the
      // /work lanes.
      { path: 'key-dates/:id', element: suspended(<KeyDateScreen />) },
      { path: 'agreements/new', element: suspended(<AgreementCreateScreen />) },
      // Registered BEFORE 'agreements/:id' so it is not swallowed as that
      // route's index child — the same ordering the property edit route uses.
      { path: 'agreements/:id/edit', element: suspended(<AgreementEditScreen />) },
      {
        path: 'agreements/:id',
        element: suspended(<AgreementDetailScreen />),
        children: [
          { index: true, element: null },
          { path: 'documents', element: suspended(<EntityDocumentsTab entityType="agreement" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="agreements" />) },
        ],
      },
      // Registered BEFORE 'utility-accounts/:id' so it is not swallowed as that
      // route's index child — the same ordering the agreement editor uses.
      { path: 'utility-accounts/:id/edit', element: suspended(<UtilityAccountEditScreen />) },
      {
        path: 'utility-accounts/:id',
        element: suspended(<UtilityAccountScreen />),
        children: [
          { index: true, element: null },
          { path: 'history', element: suspended(<RecordHistoryTab scope="utility-accounts" />) },
        ],
      },
      {
        // TKC's OWN contract with an owner (docs/11 F-2). A real record, so it
        // gets the same four tabs every other record has.
        path: 'management-agreements/:id',
        element: suspended(<ManagementAgreementScreen />),
        children: [
          { index: true, element: suspended(<ManagementTermsTab />) },
          {
            path: 'documents',
            element: suspended(<EntityDocumentsTab entityType="management_agreement" />),
          },
          {
            path: 'notes',
            element: suspended(<EntityNotesTab entityType="management_agreement" />),
          },
          {
            path: 'history',
            element: suspended(<RecordHistoryTab scope="management-agreements" />),
          },
        ],
      },
      { path: 'admin', element: suspended(<AdminHomeScreen />) },
      { path: 'admin/users', element: suspended(<AdminUsersScreen />) },
      { path: 'admin/users/:id', element: suspended(<AdminUserDetailScreen />) },
      { path: 'admin/roles', element: suspended(<AdminRolesScreen />) },
      { path: 'admin/roles/:id', element: suspended(<AdminRoleDetailScreen />) },
      { path: 'admin/reference', element: suspended(<AdminReferenceScreen />) },
      { path: 'admin/name-normalization', element: suspended(<AdminNameFixScreen />) },
      { path: 'admin/imports', element: suspended(<AdminImportScreen />) },
      { path: 'admin/audit', element: suspended(<AdminAuditScreen />) },
      // Test only (L-36): the screen says so elsewhere, and the API answers 404.
      { path: 'admin/qa', element: suspended(<AdminQaScreen />) },
      {
        path: 'admin/property-people',
        element: suspended(<AdminPeopleReconciliationScreen />),
      },
      { path: 'admin/releases', element: suspended(<AdminReleasesScreen />) },
      { path: 'admin/feature-registry', element: suspended(<AdminRegistryScreen />) },
      { path: 'documents', element: suspended(<DocumentsScreen />) },
      {
        path: 'documents/:id',
        element: suspended(<DocumentRefScreen />),
        children: [
          { index: true, element: suspended(<DocumentOverviewTab />) },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="document" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="documents" />) },
        ],
      },
      { path: 'contacts', element: suspended(<ContactsListScreen />) },
      {
        path: 'contacts/:id',
        element: suspended(<ContactDetailScreen />),
        children: [
          { index: true, element: null },
          { path: 'history', element: suspended(<RecordHistoryTab scope="contacts" />) },
        ],
      },
      {
        path: 'property-groups/:id',
        element: suspended(<GroupDetailScreen />),
        children: [
          { index: true, element: suspended(<GroupPropertiesTab />) },
          {
            path: 'documents',
            element: suspended(<EntityDocumentsTab entityType="property_group" />),
          },
          { path: 'notes', element: suspended(<EntityNotesTab entityType="property_group" />) },
          { path: 'history', element: suspended(<RecordHistoryTab scope="property-groups" />) },
        ],
      },
      { path: 'property-groups/:id/edit', element: suspended(<GroupEditScreen />) },
      { path: 'search', element: suspended(<SearchScreen />) },
      { path: 'design', element: suspended(<DesignSystemScreen />) },
      { path: 'denied', element: <DeniedScreen /> },
    ],
  },
  // /pending is rendered by the boundary for pending sessions; the direct URL
  // shows the same screen through it.
  { path: '/pending', element: <Boundary /> },
  { path: '*', element: <Navigate to="/" replace /> },
]);

/**
 * Re-exported so `LIVE_ROUTES` is importable from the router by name, as it has
 * always been, while the list itself lives in a module a CI script can read —
 * this file calls `createBrowserRouter` at module scope, which touches
 * `window`, so gate 9 cannot import it (mailbox RM-056).
 */
export { LIVE_ROUTES } from './live-routes.js';
